import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useAuth } from "../auth/AuthContext";
import {
  fetchSavedResources,
  updateSavedActivities,
  updateSavedComponentSkills,
} from "./api";

export type SavedComponentSkill = {
  id: string;
};

export type SavedActivity = {
  slug: string;
};

type SavedResourcesContextState = {
  componentSkills: SavedComponentSkill[];
  addComponentSkill: (...ids: string[]) => void;
  removeComponentSkill: (...ids: string[]) => void;
  activities: SavedActivity[];
  projectSelections: string[];
  activitiesProjectSelections: string[];
  addProjectSelection: (id: string) => void;
  removeProjectSelection: (id: string) => void;
  clearProjectSelections: () => void;
  addActivitiesProjectSelection: (id: string) => void;
  removeActivitiesProjectSelection: (id: string) => void;
  clearActivitiesProjectSelections: () => void;
  addActivity: (...slugs: string[]) => void;
  removeActivity: (...slugs: string[]) => void;
};

export const SavedResourcesContext = createContext<SavedResourcesContextState>({
  componentSkills: [],
  addComponentSkill: () => undefined,
  removeComponentSkill: () => undefined,
  activities: [],
  projectSelections: [],
  activitiesProjectSelections: [],
  addProjectSelection: () => undefined,
  removeProjectSelection: () => undefined,
  clearProjectSelections: () => undefined,
  addActivitiesProjectSelection: () => undefined,
  removeActivitiesProjectSelection: () => undefined,
  clearActivitiesProjectSelections: () => undefined,
  addActivity: () => undefined,
  removeActivity: () => undefined,
});

type SavedResourcesProviderProps = {
  children: ReactNode;
};

const SavedResourcesProvider = ({ children }: SavedResourcesProviderProps) => {
  const { user } = useAuth();
  const [savedComponentSkills, setSavedComponentSkills] = useState<
    SavedComponentSkill[]
  >([]);
  const [savedActivities, setSavedActivities] = useState<SavedActivity[]>([]);

  const [selectedComponentSkills, setSelectedComponentSkills] = useState<
    string[]
  >([]);

  const [selectedActivities, setSelectedActivities] = useState<string[]>([]);

  useEffect(() => {
    (async () => {
      if (user) {
        const { componentSkills, activities } = await fetchSavedResources(
          user.uid,
        );
        setSavedComponentSkills(componentSkills);
        setSavedActivities(activities);
      } else {
        setSavedComponentSkills([]);
        setSavedActivities([]);
      }
    })();
  }, [user]);

  const addComponentSkill = async (...ids: string[]) => {
    if (!user) {
      throw Error("User not logged in!");
    }

    const updatedComponentSkills = [
      ...savedComponentSkills,
      ...ids.map((id) => ({ id })),
    ];
    try {
      await updateSavedComponentSkills(user.uid, updatedComponentSkills);
      setSavedComponentSkills(updatedComponentSkills);
    } catch (e) {
      throw e;
    }
  };

  const removeComponentSkill = async (...ids: string[]) => {
    if (!user) {
      throw Error("User not logged in!");
    }
    const updatedComponentSkills = savedComponentSkills.filter(
      (skill) => !ids.includes(skill.id),
    );
    try {
      await updateSavedComponentSkills(user.uid, updatedComponentSkills);
      setSavedComponentSkills(updatedComponentSkills);
    } catch (e) {
      throw e;
    }
  };

  const addActivity = async (...slugs: string[]) => {
    if (!user) {
      throw Error("User not logged in!");
    }
    const updatedActivities = [
      ...savedActivities,
      ...slugs.map((slug) => ({ slug })),
    ];
    try {
      await updateSavedActivities(user.uid, updatedActivities);
      setSavedActivities(updatedActivities);
    } catch (e) {
      throw e;
    }
  };

  const removeActivity = async (...slugs: string[]) => {
    if (!user) {
      throw Error("User not logged in!");
    }
    const updatedActivities = savedActivities.filter(
      (activity) => !slugs.includes(activity.slug),
    );
    try {
      await updateSavedActivities(user.uid, updatedActivities);
      setSavedActivities(updatedActivities);
    } catch (e) {
      throw e;
    }
  };

  const addProjectSelection = (id: string) => {
    setSelectedComponentSkills((prev) => [...prev, id]);
  };
  const removeProjectSelection = (id: string) => {
    setSelectedComponentSkills((prev) => prev.filter((skill) => skill !== id));
  };
  const clearProjectSelections = () => setSelectedComponentSkills([]);

  const addActivitiesProjectSelection = (id: string) => {
    setSelectedActivities((prev) => [...prev, id]);
  };
  const removeActivitiesProjectSelection = (id: string) => {
    setSelectedActivities((prev) => prev.filter((skill) => skill !== id));
  };
  const clearActivitiesProjectSelections = () => setSelectedActivities([]);

  return (
    <SavedResourcesContext.Provider
      value={{
        componentSkills: savedComponentSkills,
        projectSelections: selectedComponentSkills,
        activities: savedActivities,
        activitiesProjectSelections: selectedActivities,
        addComponentSkill,
        removeComponentSkill,
        addProjectSelection,
        removeProjectSelection,
        clearProjectSelections,
        addActivitiesProjectSelection,
        removeActivitiesProjectSelection,
        clearActivitiesProjectSelections,
        addActivity,
        removeActivity,
      }}
    >
      {children}
    </SavedResourcesContext.Provider>
  );
};

export default SavedResourcesProvider;

export const useSavedResources = () => {
  return useContext(SavedResourcesContext);
};
